import {
  InfrastructureRequirement,
  ItemInfrastructureDto,
  infrastructureRelationship,
  infrastructureLevel,
} from '@nohara-dxdev/mt-common';
import { useMemo } from 'react';

interface UseInfrastructureRequirementsCheckParams {
  infrastructureRequirements?: InfrastructureRequirement[];
  infrastructures?: ItemInfrastructureDto[];
  currentItemId?: string;
}

interface InfrastructureRequirementCheckResult {
  /**
   * Map of infrastructure type to whether it's missing required relationships
   */
  missingRequirements: Map<string, InfrastructureRequirement | undefined>;
  /**
   * Check if a specific infrastructure type is missing required relationships
   */
  isMissingRequirement: (infrastructureType: string) => boolean;
  /**
   * Get the missing requirement for a specific infrastructure type
   */
  getMissingRequirement: (infrastructureType: string) => InfrastructureRequirement | undefined;
  /**
   * Check if all infrastructure requirements are satisfied
   */
  areAllRequirementsSatisfied: boolean;
}

/**
 * Hook to check if an item satisfies all infrastructure requirements
 *
 * @param infrastructureRequirements - Requirements from item template
 * @param infrastructures - Current item's infrastructure relationships
 * @param currentItemId - Current item ID (or 'self' for new items)
 * @returns Check results including missing requirements
 */
export const useInfrastructureRequirementsCheck = ({
  infrastructureRequirements = [],
  infrastructures = [],
  currentItemId,
}: UseInfrastructureRequirementsCheckParams): InfrastructureRequirementCheckResult => {
  const result = useMemo(() => {
    const missingRequirements = new Map<string, InfrastructureRequirement>();
    const itemId = currentItemId ?? 'self';

    // For each requirement, check if it's satisfied
    for (const requirement of infrastructureRequirements) {
      // Only check main level requirements (sub relationships are handled differently)
      if (requirement.relationshipLevel !== infrastructureLevel.main) {
        continue;
      }

      // Filter infrastructures for this infrastructure type
      const typeInfrastructures = infrastructures.filter(
        (infra) => infra.infrastructureType === requirement.infrastructureType,
      );

      if (typeInfrastructures.length === 0) {
        // No relationships for this type - requirement is missing
        missingRequirements.set(requirement.infrastructureType, requirement);
        continue;
      }

      // Check if any relationship satisfies the requirement direction
      const relationshipDirection = requirement.infrastructureRelationship;

      if (relationshipDirection === infrastructureRelationship.parent) {
        // Item must be a child (have at least one parent)
        const hasParent = typeInfrastructures.some(
          (infra) => infra.parentItemId !== itemId && infra.childItemId === itemId,
        );
        if (!hasParent) {
          missingRequirements.set(requirement.infrastructureType, requirement);
        }
      } else if (relationshipDirection === infrastructureRelationship.child) {
        // Item must be a parent (have at least one child)
        const hasChild = typeInfrastructures.some(
          (infra) => infra.parentItemId === itemId && infra.childItemId !== itemId,
        );
        if (!hasChild) {
          missingRequirements.set(requirement.infrastructureType, requirement);
        }
      } else {
        // 'both' or undefined - item must have at least one relationship in any direction
        const hasAnyRelationship = typeInfrastructures.some(
          (infra) =>
            (infra.parentItemId === itemId && infra.childItemId !== itemId) ||
            (infra.parentItemId !== itemId && infra.childItemId === itemId),
        );
        if (!hasAnyRelationship) {
          missingRequirements.set(requirement.infrastructureType, requirement);
        }
      }
    }

    return {
      missingRequirements,
      isMissingRequirement: (infrastructureType: string) =>
        missingRequirements.has(infrastructureType),
      getMissingRequirement: (infrastructureType: string) =>
        missingRequirements.get(infrastructureType),
      areAllRequirementsSatisfied: missingRequirements.size === 0,
    };
  }, [infrastructureRequirements, infrastructures, currentItemId]);

  return result;
};
